import axios from "axios";
import traceId from "../../../redux/traceId";
import {
  buildCheckoutRegisterPayload,
  getJusbillSubscriptionPath,
  resolveCheckoutPlanId,
} from "../../services/checkout/checkoutMode";
import { extractCardDetails } from "../../helpers/payment/cardDetails";

const API = process.env.REACT_APP_API_URL;
const JUSBILL = process.env.REACT_APP_JUSBILL_URL;
const CEP_API = process.env.REACT_APP_CEP_API_BASE_URL;
const PAGARME_KEY = process.env.REACT_APP_PAGARME_V5_ENCRYPTION_KEY;
const ADDON_MANAGER = process.env.REACT_APP_ADDON_MANAGER_URL;

const traceHeaders = { headers: { "x-trace-id": traceId } };

export const validateEmail = email =>
  axios.post(`${API}/validate/email`, { email });

export const validateCpf = cpfNumber =>
  axios.post(`${API}/validate/document`, { document: cpfNumber });

export const persistClient = ({ name, email }) =>
  axios.post(`${JUSBILL}/clients/persist`, { name, email }, traceHeaders);

export const requestReactivationPaymentLink = ({ email, document, plan }) =>
  axios.post(
    `${ADDON_MANAGER}/api/subscription/reactivation-link`,
    { email, document, plan },
    traceHeaders
  );

export const checkAccountStatus = ({ email }) =>
  axios.post(
    `${ADDON_MANAGER}/api/subscription/status`,
    { email },
    traceHeaders
  );

// Valida um cupom no backend (mesmo endpoint da saga LOAD_COUPOM) para
// resolver o plan_id sem alterar o estado do redux.
export const fetchCouponPlanId = async code => {
  const response = await axios.post(
    `${API}/coupons/validate`,
    { coupom: code },
    traceHeaders
  );
  return response?.data?.data?.plan_id;
};

export const searchAddressByCep = async code => {
  try {
    const response = await axios.get(`${CEP_API}/${code}/json/`, {
      timeout: 5000,
    });
    const data = response.data;
    if (data.erro === "true" || data.erro === true) {
      return { success: false, error: "Não foi possível localizar o CEP" };
    }
    return {
      success: true,
      data: {
        address: data.logradouro || "",
        district: data.bairro || "",
        city: data.localidade || "",
        state: data.uf || "",
      },
    };
  } catch (error) {
    const status = error?.response?.status;
    const isTimeout = error?.code === "ECONNABORTED";
    if (isTimeout || (status && status >= 500)) {
      return { success: true, fallback: true };
    }
    return { success: false, error: "Não foi possível localizar o CEP" };
  }
};

export const createPagarmeToken = async values => {
  const cardDetails = extractCardDetails({
    cardNumber: values.card_number,
    cardExpiration: values.card_expiration,
  });

  const cardPayload = {
    type: "card",
    card: {
      number: cardDetails.cleanCardNumber,
      holder_name: values.cardholder_name,
      exp_month: parseInt(cardDetails.expMonth, 10),
      exp_year: parseInt(cardDetails.expYearShort, 10),
      cvv: values.card_cvv,
      label: "Cartão do Cliente",
    },
  };
  const { data } = await axios.post(
    `https://api.pagar.me/core/v5/tokens?appId=${PAGARME_KEY}`,
    cardPayload
  );
  return data;
};

export const getClientIp = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });
    const data = await res.json();
    return data.ip;
  } catch {
    return "unknown";
  } finally {
    clearTimeout(timeoutId);
  }
};

export const saveCardOnBackend = async ({ token, values, ip }) => {
  const cardDetails = extractCardDetails({
    cardNumber: values.card_number,
    cardExpiration: values.card_expiration,
  });

  return axios.post(
    `${JUSBILL}/client-cards/pagarme-v5`,
    {
      customerId: values.createdCustomerId,
      email: values.email,
      name: values.name,
      document: values.document_number.replace(/[^0-9]/g, ""),
      phone: values.phone,
      address: {
        zipCode: values.postal_code,
        state: values.state?.state,
        address: values.address,
        number: values.address_number,
        neighborhood: values.district,
        city: values.city?.name,
        complement: values.address_complement,
      },
      card: {
        token,
        holderName: values.cardholder_name,
        lastDigits: cardDetails.lastDigits,
        firstDigits: cardDetails.firstDigits,
        expirationMonth: cardDetails.expMonth,
        expirationYear: cardDetails.expYearShort,
        brand: cardDetails.brand,
      },
      metadata: {
        id: ip ?? "unknown",
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    traceHeaders
  );
};

const buildPhone = phone => {
  const raw = phone.replace(/\D/g, "");
  return { ddd: raw.slice(0, 2), number: raw.slice(2) };
};

const buildCustomer = values => ({
  email: values.email,
  name: values.name,
  document_number: values.document_number,
  address: {
    street: values.address,
    street_number: values.address_number,
    complement: values.address_complement,
    neighborhood: values.district,
    city: values.city?.name,
    state: values.state?.state,
    zipcode: values.postal_code,
  },
  phone: buildPhone(values.phone),
});

export const createCardSubscription = ({ values, coupom, cardData }) =>
  axios.post(
    `${JUSBILL}${getJusbillSubscriptionPath({ paymentMethod: "credit_card" })}`,
    {
      plan_id: resolveCheckoutPlanId({ coupom, plan: values.plan }),
      postback_url: null,
      coupom: values.coupom,
      customer: buildCustomer(values),
      payment_method: "credit_card",
      card_id: cardData.id,
      provider: "pagarme_v5",
      register: buildCheckoutRegisterPayload({ values }),
    },
    traceHeaders
  );

export const createPixSubscription = ({ values, coupom }) =>
  axios.post(
    `${JUSBILL}${getJusbillSubscriptionPath({ paymentMethod: "pix" })}`,
    {
      coupom: values.coupom,
      plan_id: resolveCheckoutPlanId({ coupom, plan: values.plan }),
      postback_url: null,
      customer: buildCustomer(values),
      payment_method: "pix",
      provider: "pagarme_v5",
      register: buildCheckoutRegisterPayload({ values }),
    },
    traceHeaders
  );

export const fetchSubscription = id =>
  axios.get(`${JUSBILL}/subscriptions/${id}`, traceHeaders);

export const provisionCapicandia = ({ values, subscriptionId, planVariant }) =>
  axios.post(
    `${ADDON_MANAGER}/api/capicandia/provision`,
    {
      email: values.email,
      name: values.name,
      subscription_id: subscriptionId,
      plan_variant: planVariant,
    },
    traceHeaders
  );

// Cadastro de aluno de infoprodutor (spec checkout-jusfy-cupom-trial §5.1).
// Vai para o addon-manager, que persiste em addon_checkout e provisiona no
// infoprodutores-service. Só ids opacos do cartão (uuid Jusbill + card_xxx
// Pagar.me) — nunca PAN/CVV. Sem ip no body: o servidor usa req.ip.
export const signupInfoprodutores = ({ values, createCard, coupon }) => {
  const cardDetails = extractCardDetails({
    cardNumber: values.card_number,
    cardExpiration: values.card_expiration,
  });

  return axios.post(
    `${ADDON_MANAGER}/api/infoprodutores/signup`,
    {
      coupon: coupon.code,
      plan_variant: coupon.plan_variant,
      name: values.name,
      email: values.email,
      password: values.password,
      document: (values.document_number || "").replace(/\D/g, ""),
      phone: values.phone,
      card: {
        jusbill_card_id: createCard.data.id,
        provider_card_id: createCard.data.token,
        last_four: cardDetails.lastDigits,
        brand: cardDetails.brand,
        exp_month: cardDetails.expMonth,
        exp_year: cardDetails.expYearShort,
        holder_name: values.cardholder_name,
      },
      metadata: {
        user_agent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    { ...traceHeaders, timeout: 15000 }
  );
};
